import React, { useState, useEffect, useCallback } from 'react';
import { Table } from 'react-bootstrap';
import { getDocumentPackages, addDocumentPackage, getAvailablePackages } from '../../../usecases/user-operations-usecase';
import PaginatedTable from '../../atoms/pagination/pagination';
import ButtonLoading from '../../atoms/buttonLoading/view';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { BiPlusCircle, BiHistory } from 'react-icons/bi';
import './style.css';

const UserDocumentPackages = ({ userId }) => {
    const [userPackages, setUserPackages] = useState([]);
    const [catalogPackages, setCatalogPackages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(10);

    const [selectedPackageId, setSelectedPackageId] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const fetchUserPackages = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const response = await getDocumentPackages(userId, page, pageSize);
            if (response && response.success) {
                setUserPackages(response.data.data || []);
                setTotalItems(response.data.total || 0);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Error fetching user packages:', error);
            dispatchCustomEvent('popup', { message: 'Error al cargar paquetes del usuario', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [userId, pageSize]);

    const fetchCatalog = useCallback(async () => {
        try {
            const response = await getAvailablePackages(1, 100);
            if (response && response.success) {
                setCatalogPackages(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching catalog:', error);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            fetchUserPackages(1);
            fetchCatalog();
        }
    }, [userId, fetchUserPackages, fetchCatalog]);

    const handleAddPackage = async () => {
        if (!selectedPackageId) {
            dispatchCustomEvent('popup', { message: 'Seleccione un tipo de paquete', type: 'error' });
            dispatchCustomEvent('loading-btn');
            return;
        }

        try {
            const payload = {
                users_id: parseInt(userId),
                marketplace_package_id: parseInt(selectedPackageId),
                quantity: parseInt(quantity)
            };

            const response = await addDocumentPackage(payload);
            if (response && response.success) {
                dispatchCustomEvent('popup', { message: 'Paquete añadido correctamente' });
                setSelectedPackageId('');
                setQuantity(1);
                fetchUserPackages(1);
            } else {
                dispatchCustomEvent('popup', { message: response.message || 'Error al añadir paquete', type: 'error' });
            }
        } catch (error) {
            console.error('Error adding package:', error);
            dispatchCustomEvent('popup', { message: 'Error al procesar la solicitud', type: 'error' });
        } finally {
            dispatchCustomEvent('loading-btn');
        }
    };

    return (
        <div className="user-packages-container">
            <div className="add-package-card">
                <h4 className="section-title"><BiPlusCircle /> Añadir Paquete</h4>
                <form className="package-form-grid">
                    <div className="form-field">
                        <label>Paquete</label>
                        <select
                            value={selectedPackageId}
                            onChange={(e) => setSelectedPackageId(e.target.value)}
                            className="form-select custom-input"
                            style={{ width: '100%', display: 'block' }}
                            required
                        >
                            <option value="">-- Seleccionar --</option>
                            {catalogPackages.map(pkg => (
                                <option key={pkg.id} value={pkg.id}>
                                    {pkg.name} ({pkg.quantity} docs)
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-field">
                        <label>Cant. Paquetes</label>
                        <input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            className="form-control custom-input"
                            style={{ width: '100%', display: 'block' }}
                            required
                        />
                    </div>
                    <div className="form-field">
                        <ButtonLoading
                            id="add-doc-package-btn"
                            onClick={handleAddPackage}
                        >
                            Añadir
                        </ButtonLoading>
                    </div>
                </form>
            </div>

            <div className="packages-section">
                <h4 className="section-title"><BiHistory /> Historial</h4>

                {isLoading ? (
                    <div className="empty-state">Cargando...</div>
                ) : userPackages.length === 0 ? (
                    <div className="empty-state">Sin paquetes registrados.</div>
                ) : (
                    <>
                        <div className="packages-table-container">
                            <Table responsive borderless className="packages-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Paquete</th>
                                        <th>Total Docs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userPackages.map(upkg => (
                                        <tr key={upkg.id}>
                                            <td>{new Date(upkg.created_at).toLocaleDateString()}</td>
                                            <td>
                                                <span className="package-badge">
                                                    {upkg.package_details?.name || 'Carga Directa'}
                                                </span>
                                            </td>
                                            <td><strong>{upkg.quantity}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex justify-content-center mt-3">
                            <PaginatedTable
                                id="user-packages"
                                itemsCountPerPage={pageSize}
                                totalItemsCount={totalItems}
                                currentPage={currentPage}
                                getFunction={(page) => fetchUserPackages(page)}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserDocumentPackages;
